import { getCookieDomainCandidates } from '~/composables/useCookieOptions';
import { LEGACY_AUTH_COOKIE_KEYS, STORAGE_KEYS } from '~/constants/storageKeys';
import { isAuthCallbackPath, toComparablePath } from '~/utils/authRoutes';
import { checkJwtOrgScope, isJwtExpired } from '~/utils/jwt';

/**
 * 認證 cookie 一次性遷移：`Authorization` / `RefreshToken` → `xsenal_*`（host-only）
 *
 * 舊名稱與後端在 Google 登入時寫在父網域上的 HttpOnly cookie 同名（見 constants/storageKeys.ts）。
 * 同名並存時瀏覽器兩份都會送出、SSR 只讀得到較早建立的那份，於是切換組織後永遠讀回舊 token。
 * 前端改用 `xsenal_` 前綴之後就不再碰那個名字了，這支負責把既有使用者平順地搬過去。
 *
 * 【為什麼不能直接搬讀到的第一筆】`document.cookie` 只給值、不給 scope，而同名的
 * `Authorization` 可能有兩份非 HttpOnly 的來源：本 host 自己寫的（正確）、以及 2026-08-04
 * 改成 host-only 之前寫在父網域上的殘留（可能是別的子網域／別的環境、別的組織的舊 token）。
 * 依 RFC 6265 §5.4 同名同 path 是「建立時間早的排前面」，父網域那份反而通常排第一，
 * 盲採第一筆等於優先把最舊、最可能錯環境的 token 升格成新名稱。
 *
 * 所以流程改成先消除 scope 歧義、再驗證、才搬移：
 *   1. 把舊名稱（與 ORGUUID）在所有 domain scope 上的那幾份先刪掉
 *   2. 此時 `document.cookie` 裡剩下的同名值必然是 host-only —— 也就是本環境自己寫的那份
 *      （後端寫的父網域那份帶 HttpOnly，本來就讀不到也刪不掉，留著無害，
 *        真正清除仍由登出的 server route 負責）
 *   3. 唯一、refresh token 未過期、組織 scope 不衝突時才整組搬移；
 *      任一條不成立就整組放棄並清乾淨，讓使用者走正常的重新登入，不留半套 session
 *
 * 【例外：OAuth 回調頁不跑】Google 登入時後端寫下的舊名稱 cookie 還沒交接給前端，
 * 那是那條流程唯一的 token 來源（見 `server/routes/_auth/oauth-handoff.post.ts`）。
 * 這支 plugin 是 `enforce: 'pre'`，跑在回調頁的 onMounted 之前，若在這裡把舊名稱刪掉，
 * 交接就撲空、Google 登入直接被導回登入頁。回調頁上的清理由交接本身負責。
 */
export default defineNuxtPlugin({
  name: 'auth-cookie-migration',
  enforce: 'pre',
  setup() {
    if (!import.meta.client) return;

    // 回調頁上的舊名稱 cookie 是待交接的憑證，不是待清除的殘留
    if (isAuthCallbackPath(toComparablePath(window.location.pathname))) return;

    const domains = getCookieDomainCandidates(window.location.hostname);
    const maxAge = 60 * 60 * 24 * 7;
    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';

    /** 名稱對應：舊 → 新 */
    const renamed = [
      [LEGACY_AUTH_COOKIE_KEYS.TOKEN, STORAGE_KEYS.TOKEN],
      [LEGACY_AUTH_COOKIE_KEYS.REFRESH_TOKEN, STORAGE_KEYS.REFRESH_TOKEN],
    ] as const;

    /** 讀出某個名稱底下所有的 cookie 值（同名可能同時存在 host-only 與 domain 兩份） */
    const readAll = (name: string): string[] =>
      document.cookie
        .split(';')
        .map((part) => part.trim())
        .filter((part) => part.startsWith(`${name}=`))
        .map((part) => part.slice(name.length + 1))
        .filter(Boolean);

    /** 刪掉某個名稱在所有 domain scope 上的那幾份，只留下 host-only（HttpOnly 的刪不動） */
    const deleteDomainScoped = (name: string) => {
      for (const domain of domains) {
        document.cookie = `${name}=; ${expired}; path=/; domain=${domain}`;
      }
    };

    try {
      // 舊版把 ORGUUID 也寫在父網域上，先清掉——下面要拿 host-only 那份來驗證組織 scope，
      // 讀到父網域的殘留會把正確的 token 誤判成別的組織。
      deleteDomainScoped(STORAGE_KEYS.ORGUUID);

      for (const [legacyKey] of renamed) deleteDomainScoped(legacyKey);

      // 到這裡讀到的舊名稱值必然是 host-only 的，也就是本 host（本環境）自己寫的那份
      const hostOnly = new Map(renamed.map(([legacyKey]) => [legacyKey, readAll(legacyKey)]));

      const legacyToken = hostOnly.get(LEGACY_AUTH_COOKIE_KEYS.TOKEN) ?? [];
      const legacyRefreshToken = hostOnly.get(LEGACY_AUTH_COOKIE_KEYS.REFRESH_TOKEN) ?? [];
      const expectedOrgUuid = readAll(STORAGE_KEYS.ORGUUID)[0] ?? '';

      // 整組一起判斷，避免只搬到一半留下半套 session（有 access token 卻沒有 refresh token，
      // 反之亦然），那只會換來一輪 401 → refresh 失敗 → 登出。
      const migratable =
        // 同名仍多於一份代表 scope 無法判定（例如 path 不同的殘留），寧可不搬
        legacyToken.length <= 1 &&
        legacyRefreshToken.length <= 1 &&
        // refresh token 過期的話，搬過去也只是啟動即被踢回登入頁
        !legacyRefreshToken.some((value) => isJwtExpired(value)) &&
        // token 帶的組織與目前選定的組織衝突時不搬（認不出組織欄位會回 unknown，照樣放行）
        !legacyToken.some((value) => checkJwtOrgScope(value, expectedOrgUuid) === 'mismatch');

      for (const [legacyKey, key] of renamed) {
        const value = hostOnly.get(legacyKey)?.[0];

        // 新名稱還沒有值時才搬，已經有值代表使用者早就遷移過，舊的那份是過期殘留
        if (migratable && value && readAll(key).length === 0) {
          document.cookie = `${key}=${value}; path=/; max-age=${maxAge}`;
        }

        // 清掉舊名稱的 host-only 殘留（domain 那幾份上面已經刪過）
        document.cookie = `${legacyKey}=; ${expired}; path=/`;
      }
    } catch (error) {
      console.warn('Auth cookie migration failed:', error);
    }
  },
});
